import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { auth, db, perf } from '../firebase';
import {
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  signInWithRedirect
} from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { useNavigate, useLocation } from 'react-router-dom';
import { trace } from 'firebase/performance';
import unifiedGoogleTokenManager from '../services/UnifiedGoogleTokenManager';

const AuthContext = createContext(null);

// Cache for user data to prevent unnecessary Firestore reads
const userDataCache = new Map();

// Constants
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

// Helper function for delay
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const initialized = useRef(false);
  const authStateUnsubscribe = useRef(null);
  const userDocUnsubscribe = useRef(null);
  const retryCount = useRef(0);
  // Ref for current pathname so auth callbacks (e.g. on token refresh) always see latest route.
  // Without this, handleUserAuthentication can run with a stale pathname and redirect users
  // from e.g. /app/theme back to /app when Firebase re-fires onAuthStateChanged.
  const locationPathnameRef = useRef(location.pathname);
  locationPathnameRef.current = location.pathname;

  // Cleanup function for subscriptions
  const cleanup = useCallback(() => {
    if (authStateUnsubscribe.current) {
      authStateUnsubscribe.current();
    }
    if (userDocUnsubscribe.current) {
      userDocUnsubscribe.current();
    }
  }, []);

  // Memoize user data fetching with retry logic
  const fetchUserData = useCallback(async (uid, email, attempt = 1) => {
    const t = trace(perf, 'fetchUserData');
    t.start();

    try {
      // Check cache first
      if (userDataCache.has(uid)) {
        const cachedData = userDataCache.get(uid);
        const cacheAge = Date.now() - cachedData.timestamp;
        // Use cache if it's less than 5 minutes old
        if (cacheAge < 5 * 60 * 1000) {
          t.stop();
          return cachedData.data;
        }
      }

      const userDoc = await getDoc(doc(db, 'users', uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        // Update cache
        userDataCache.set(uid, {
          data: userData,
          timestamp: Date.now()
        });
        t.stop();
        return userData;
      }

      // Check authorized users for new users
      const authorizedDoc = await getDoc(doc(db, 'authorized_users', email));
      
      if (authorizedDoc.exists()) {
        const authData = authorizedDoc.data();
        const newUserData = {
          email,
          role: authData.role,
          permissions: authData.permissions || [],
          createdAt: new Date()
        };
        
        await setDoc(doc(db, 'users', uid), newUserData);
        
        // Update cache
        userDataCache.set(uid, {
          data: newUserData,
          timestamp: Date.now()
        });
        
        t.stop();
        return newUserData;
      }

      throw new Error('unauthorized');
    } catch (error) {
      t.stop();
      console.error(`Attempt ${attempt} failed:`, error);
      
      if (attempt < MAX_RETRIES && error.code !== 'permission-denied') {
        await delay(RETRY_DELAY * attempt);
        return fetchUserData(uid, email, attempt + 1);
      }
      
      throw error;
    }
  }, []);

  // Memoize authentication handler with performance tracking
  const handleUserAuthentication = useCallback(async (currentUser) => {
    const t = trace(perf, 'userAuthentication');
    t.start();

    try {
      if (!currentUser) {
        setUser(null);
        const currentPath = locationPathnameRef.current;
        if (currentPath !== '/login') {
          navigate('/login', { replace: true });
        }
        t.stop();
        return;
      }

      const userData = await fetchUserData(currentUser.uid, currentUser.email);
      
      // Update last login timestamp in users collection, but don't store the token
      await setDoc(doc(db, 'users', currentUser.uid), {
        ...userData,
        lastLoginAt: new Date()
      }, { merge: true });
      
      // Set up real-time listener for user document, but only if one doesn't already exist
      if (!userDocUnsubscribe.current) {
        // Set up a listener with proper error handling and cleanup
        try {
          console.log('Setting up user document listener for', currentUser.uid);
          userDocUnsubscribe.current = onSnapshot(
            doc(db, 'users', currentUser.uid), 
            { includeMetadataChanges: false },  // Only listen for actual data changes, not metadata
            (snapshot) => {
          if (snapshot.exists()) {
            const updatedUserData = snapshot.data();
            setUser(prevUser => ({
              ...prevUser,
              ...updatedUserData,
              themePreferences: updatedUserData.themePreferences
            }));
          }
            },
            (error) => {
              console.error('User document listener error:', error);
              // Don't crash on listener errors
            }
          );
        } catch (error) {
          console.error('Error setting up user document listener:', error);
          // Continue without the listener if it fails
        }
      }
      
      // Only call setUser if this is a new user session (uid changed) or first login.
      // Avoids creating a new user object reference on every token refresh (~hourly),
      // which would otherwise trigger unnecessary hotel re-fetches in AppContent.
      setUser(prevUser => {
        if (prevUser?.uid === currentUser.uid) {
          return prevUser; // Same user, same session — no re-render needed
        }
        return {
          ...currentUser,
          ...userData,
          lastLoginAt: new Date()
        };
      });

      // Improved navigation logic: only redirect to /app when actually on a public route.
      // Use ref so we always read current path (avoids redirecting from /app/theme etc.
      // when Firebase re-fires onAuthStateChanged e.g. on token refresh).
      const currentPath = locationPathnameRef.current;
      const publicRoutes = ['/login', '/']; // Add any other public routes here
      // #region agent log
      try{const _l=JSON.parse(localStorage.getItem('_dbg_nav')||'[]');_l.push({t:Date.now(),src:'authHandler',path:currentPath,isPublic:publicRoutes.includes(currentPath),uid:currentUser?.uid,willNav:publicRoutes.includes(currentPath)});localStorage.setItem('_dbg_nav',JSON.stringify(_l.slice(-50)));}catch(e){}
      // #endregion
      if (publicRoutes.includes(currentPath)) {
        navigate('/app', { replace: true });
      }

      t.stop();
    } catch (error) {
      t.stop();
      
      if (error.message === 'unauthorized') {
        // Handle unauthorized silently without console error
        await firebaseSignOut(auth);
        setUser(null);
        setError('unauthorized');
        navigate('/login', { replace: true });
        return; // Return early to prevent error from propagating
      } 
      
      if (error.code === 'permission-denied') {
        setError('permission-denied');
        navigate('/login', { replace: true });
        return; // Return early to prevent error from propagating
      }
      
      // Only log other types of errors
      console.error('Authentication error:', error);
      throw error;
    }
  }, [fetchUserData, navigate]);

  // Initialize auth state listener with error boundary
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const initializeAuth = async () => {
      const t = trace(perf, 'authInitialization');
      t.start();

      try {
        await setPersistence(auth, browserLocalPersistence);
        
        authStateUnsubscribe.current = onAuthStateChanged(auth, async (currentUser) => {
          try {
            await handleUserAuthentication(currentUser);
          } catch (error) {
            // Only retry for non-unauthorized errors
            if (error.message !== 'unauthorized' && error.code !== 'permission-denied') {
              if (retryCount.current < MAX_RETRIES) {
                retryCount.current++;
                await delay(RETRY_DELAY * retryCount.current);
                await handleUserAuthentication(currentUser);
              }
            }
          } finally {
            setLoading(false);
          }
        });

        t.stop();
      } catch (error) {
        t.stop();
        // Only log initialization errors that aren't related to authorization
        if (error.message !== 'unauthorized' && error.code !== 'permission-denied') {
          console.error('Auth initialization error:', error);
        }
        setError(error.message);
        setLoading(false);
      }
    };

    initializeAuth();

    return cleanup;
  }, [handleUserAuthentication, cleanup]);

  // Reset retry count when auth state changes successfully
  useEffect(() => {
    if (user) {
      retryCount.current = 0;
    }
  }, [user]);

  // Memoize sign in handler with performance tracking
  const signInWithGoogle = useCallback(async () => {
    const t = trace(perf, 'googleSignIn');
    t.start();

    try {
      setError(null);
      const provider = new GoogleAuthProvider();
      
      // Note: Drive, Contacts, and Directory scopes are NOT requested here.
      // Users who need these features will authenticate when visiting those pages.
      
      // Configure provider settings - only show consent when necessary
      provider.setCustomParameters({
        prompt: 'select_account', // Only show account selection, not consent screen
        access_type: 'offline'
      });

      // Configure auth settings
      auth.settings.appVerificationDisabledForTesting = false;
      
      try {
        const result = await signInWithPopup(auth, provider).catch(error => {
          if (error.code === 'auth/popup-closed-by-user' || 
              error.code === 'auth/cancelled-popup-request') {
            return null;
          }
          throw error;
        });
        
        if (!result) {
          t.stop();
          return null;
        }

        // Extract the Google OAuth credentials
        const credential = GoogleAuthProvider.credentialFromResult(result);
        const accessToken = credential.accessToken;
        
        // Store tokens using unified token manager (only basic auth token, no Drive/Contacts scopes)
        try {
          const tokenData = {
            accessToken: accessToken,
            refreshToken: credential.refreshToken,
            clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
            scope: '', // No additional scopes requested at login
            token_type: 'Bearer'
          };
          
          await unifiedGoogleTokenManager.storeToken(result.user.uid, tokenData, 3600);
          console.log('✅ Basic auth tokens stored in unified token manager');
        } catch (tokenError) {
          console.error('Failed to store tokens in unified manager:', tokenError);
        }

        // Immediately handle the authentication
        await handleUserAuthentication(result.user);
        
        t.stop();
        return result;
      } catch (popupError) {
        console.warn('Popup error:', popupError);
        // Try alternative sign-in method if popup fails
        if (popupError.code === 'auth/popup-blocked') {
          // We need to modify redirect flow as well to capture the token
          const result = await signInWithRedirect(auth, provider);
          await handleUserAuthentication(result.user);
          return result;
        }
        throw popupError;
      }
    } catch (error) {
      t.stop();
      console.error('Sign in error:', error);
      
      const errorMap = {
        'auth/popup-blocked': 'popup-blocked',
        'auth/cancelled-popup-request': 'cancelled',
        'auth/network-request-failed': 'network-error',
        'auth/unauthorized-domain': 'unauthorized-domain',
        'permission-denied': 'unauthorized'
      };
      
      setError(errorMap[error.code] || 'default');
      throw error;
    }
  }, [handleUserAuthentication]);

  // Memoize sign out handler with performance tracking
  const signOut = useCallback(async (reason = 'user_initiated') => {
    const t = trace(perf, 'signOut');
    t.start();

    try {
      // Track logout analytics before signing out
      try {
        const sessionStart = parseInt(localStorage.getItem('session_start')) || Date.now();
        const sessionDuration = Date.now() - sessionStart;
        
        // Direct analytics tracking since we can't use hooks here
        if (window.gtag) {
          window.gtag('event', 'logout', {
            logout_time: new Date().toISOString(),
            session_duration: sessionDuration,
            session_duration_minutes: Math.round(sessionDuration / 60000),
            logout_reason: reason,
            user_id: user?.uid || 'anonymous'
          });
        }
        
        // Clear session data
        localStorage.removeItem('session_start');
      } catch (analyticsError) {
        console.warn('Failed to track logout analytics:', analyticsError);
      }
      
      cleanup();
      await firebaseSignOut(auth);
      setUser(null);
      // Clear user data cache and unified tokens on sign out
      userDataCache.clear();
      unifiedGoogleTokenManager.clearAllTokens();
      navigate('/login', { replace: true });
      t.stop();
    } catch (error) {
      t.stop();
      console.error('Sign out error:', error);
      setError('signout-failed');
      throw error;
    }
  }, [navigate, cleanup, user]);

  const value = {
    user,
    loading,
    error,
    signInWithGoogle,
    signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 